import React, { Fragment, useRef } from 'react';
import { bool, element, func, elementType, string, oneOfType, array, object } from 'prop-types';
import { FocusScope, useFocusWithin, useKeyboard, useFocusRing } from 'react-aria';
import clsx from 'clsx';
import { CaretDown, CaretUp } from '@nike/nike-design-system-icons';
import { BODEGA_DEFAULTS, IS_FOCUSSED } from '../../constants';
import {
  StyledDetails,
  dropDownAccessibilityButton,
  summaryCaret,
  accountIconClass,
} from './styles';
import useBodega from '../../utils/hooks/bodegaHook';
import templateString from '../../utils/templateString';

/**
 * conditionally renders caret up or down based on menu state
 * @param {object} param
 * @param {boolean} param.isMenuOpen ? up : down
 * @param {object} param.passedProps props to pass to caret
 * @returns jsx element
 */
const Caret = ({ isMenuOpen, passedProps }) =>
  isMenuOpen ? <CaretUp {...passedProps} /> : <CaretDown {...passedProps} />;

const FocusWrapper = ({ children, isMenuOpen, isFocusVisible }) => {
  return isMenuOpen ? (
    <FocusScope autoFocus={isFocusVisible} restoreFocus>
      {children}
    </FocusScope>
  ) : (
    // For SEO purposes.
    <Fragment>{children}</Fragment>
  );
};

/**
 * reusable dropdown menu
 * @param {String} props.id landmark
 * @param {JSX element} props.children content inside menu
 * @param {String} props.detailsContentCustomClass css class string
 * @param {String} props.detailSummaryRef Element to shift focus to if escape key is used to close
 * menu
 * @returns {JSX element}
 */
const DropDownMenu = props => {
  const {
    testId,
    children,
    menuLink,
    menuLabel,
    MenuContainerComponent,
    detailsContentCustomClass,
    isMenuOpen,
    setMenuOpen,
    containerClassName,
    styledSummaryText,
  } = props;
  const { isFocusVisible, focusProps } = useFocusRing();
  const { MENU } = BODEGA_DEFAULTS;
  const accountIcon = menuLabel === 'Account Menu';
  // lets us close menu with keyboard and return focus to open menu button
  const detailSummaryRef = useRef(null);
  const { keyboardProps } = useKeyboard({
    onKeyDown: e => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        detailSummaryRef.current.focus();
      }
    },
  });
  const { keyboardProps: summaryOnPressProps } = useKeyboard({
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === 'Space') {
        setMenuOpen(!isMenuOpen);
        e.nativeEvent.preventDefault();
      } else if (e.shiftKey && e.key === 'Tab' && isMenuOpen) {
        /*
          This scenario exists because when voice over (vo) ask you to execute {ctrl+option+space} to
          open the menu, it will not, navigate into the menu and highlight the first item. This is
          is triggered by the useFocusRing, but apparently, when vo is on, it isn't recognized as
          being focused. It is a weird behavior.
         */
        setMenuOpen(false);
      }
    },
  });

  const menuLabelComplete = templateString(useBodega('nav_menu_aria_label', MENU), {
    menu: props.menuLabel || '',
  });

  // closes menu when focus leaves container
  const { focusWithinProps } = useFocusWithin({
    onBlurWithin: () => setMenuOpen(false),
  });

  // if there is no dropdown content just return the link within the provided container
  if (!children) {
    return (
      <MenuContainerComponent className={containerClassName} data-testid={testId}>
        {menuLink}
      </MenuContainerComponent>
    );
  }

  return (
    <MenuContainerComponent
      className={containerClassName}
      data-testid={testId}
      onMouseEnter={() => setMenuOpen(true)}
      onMouseLeave={() => setMenuOpen(false)}
    >
      {menuLink}
      <StyledDetails
        className={clsx(isMenuOpen && IS_FOCUSSED)}
        // Needed for voice over's control+option+space to work.
        onToggle={e => setMenuOpen(e.currentTarget.open)}
        open={isMenuOpen}
      >
        <summary
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
          aria-label={menuLabelComplete}
          className={clsx(dropDownAccessibilityButton, accountIcon && accountIconClass)}
          ref={detailSummaryRef}
          role="menuitem"
          /*
            Current version of Jest/React combination doesn't support the details
            to the extent that our test will pass successfully. The tabIndex is there
            so that the test can pick it as a button. When we updated React this
            property can be removed.
           */
          tabIndex={0}
          {...focusProps}
          {...summaryOnPressProps}
        >
          {styledSummaryText}
          <Caret
            isMenuOpen={isMenuOpen}
            passedProps={{ 'aria-hidden': 'true', size: 's', className: summaryCaret }}
          />
        </summary>
        <FocusWrapper isFocusVisible={isFocusVisible} isMenuOpen={isMenuOpen}>
          <div
            className={detailsContentCustomClass}
            data-testid="dropdown-details-container"
            {...focusWithinProps}
            {...keyboardProps}
          >
            {children}
          </div>
        </FocusWrapper>
      </StyledDetails>
    </MenuContainerComponent>
  );
};

export default DropDownMenu;

DropDownMenu.propTypes = {
  children: oneOfType([element, array]),
  detailsContentCustomClass: string,
  isMenuOpen: bool,
  testId: string,
  setMenuOpen: func,
  menuLink: element,
  MenuContainerComponent: elementType,
  containerClassName: string,
  menuLabel: string,
  styledSummaryText: element,
};

FocusWrapper.propTypes = {
  children: oneOfType([element, array]),
  isMenuOpen: bool,
  isFocusVisible: bool,
};

Caret.propTypes = {
  isMenuOpen: bool,
  passedProps: object,
};
